import type {
  EditPositionFillRequestCommand,
  TransitPositionFillRequestCommand,
  WorkIndicatorResponse,
} from 'domain/shared/positionFillRequest';
import type { ListResponse, LoadingParams } from 'framework/list';
import type { ApiRequestOptions } from 'framework/api';
import type { CollectionPageResponse } from 'framework/ViewTypes/hooks';
import { useRecruitmentApi } from 'infrastructure/api';
import { POSITION_FILL_REQUEST_STATUS } from './constants';
import type { PositionFillRequest, PositionFillRequestListItem } from './types';
import { enrichPositionFillRequests } from './utils/positionFillRequestDisplay';
import { useBusinessFunctionService } from 'domain/shared/businessFunction';
import { useTechnologyService } from 'domain/shared/career';
import { usePositionService, useOfficeService } from 'domain/shared/employee';

const usePositionFillRequestsService = () => {
  const { findAll: findAllTechnologies } = useTechnologyService();
  const { findAll: findAllPositions } = usePositionService();
  const { findAll: findAllOffices } = useOfficeService();
  const { findAll: findAllBusinessFunctions } = useBusinessFunctionService();

  const { sendGetRequest, sendPostRequest, sendPutRequest, sendPatchRequest } = useRecruitmentApi();

  const enrichPfrList = async (positionSlots: PositionFillRequest[]): Promise<PositionFillRequestListItem[]> => {
    const [positions, technologies, offices, businessFunctions] = await Promise.all([
      findAllPositions(),
      findAllTechnologies(),
      findAllOffices(),
      findAllBusinessFunctions(),
    ]);

    return enrichPositionFillRequests(positionSlots, {
      positions,
      technologies,
      offices,
      businessFunctions,
    }) as PositionFillRequestListItem[];
  };

  const search = async (
    loadParams: LoadingParams,
    options?: ApiRequestOptions,
  ): Promise<CollectionPageResponse<PositionFillRequestListItem>> => {
    const response = await sendPostRequest('/position-fill-requests/search?extendPosition=true', loadParams, options);
    const page = (await response.json()) as ListResponse<PositionFillRequest>;

    const pfrs = await enrichPfrList(page.result ?? []);

    return {
      result: pfrs,
      pageNumber: page.pageNumber ?? loadParams.pageNumber,
      totalPages: page.totalPages ?? 0,
      totalElements: page.totalElements ?? page.result?.length ?? 0,
    };
  };

  const getById = async (id: string): Promise<PositionFillRequestListItem | undefined> => {
    const response = await sendGetRequest(`/position-fill-requests/${id}?extendPosition=true`);
    const request = (await response.json()) as PositionFillRequest;

    const [pfr] = await enrichPfrList([request]).catch(cause => {
      throw new Error('Could not load position details.', { cause });
    });

    return pfr;
  };

  const transitPositionFillRequest = async (
    id: string,
    command: TransitPositionFillRequestCommand,
  ): Promise<PositionFillRequest> => {
    const response = await sendPatchRequest(`/position-fill-requests/${id}/transit`, command);

    return response.json();
  };

  const makeApprovalDecision = (id: string, decision: 'APPROVE' | 'REJECT') =>
    transitPositionFillRequest(id, {
      status: decision === 'APPROVE' ? POSITION_FILL_REQUEST_STATUS.PENDING : POSITION_FILL_REQUEST_STATUS.REJECTED,
    });

  const cancelPositionFillRequest = (id: string) =>
    transitPositionFillRequest(id, { status: POSITION_FILL_REQUEST_STATUS.CANCELLED });

  const editPositionFillRequest = async (
    id: string,
    command: EditPositionFillRequestCommand,
  ): Promise<PositionFillRequest> => {
    const response = await sendPutRequest(`/position-fill-requests/${id}`, command);

    return response.json();
  };

  const getWorkIndicator = async (): Promise<WorkIndicatorResponse> => {
    const request = await sendGetRequest('/position-fill-requests/work-indicator');
    const workIndicator = (await request.json()) as WorkIndicatorResponse;

    return workIndicator ?? { hasWorkToDo: false };
  };

  return {
    search,
    getById,
    getWorkIndicator,
    transitPositionFillRequest,
    makeApprovalDecision,
    cancelPositionFillRequest,
    editPositionFillRequest,
  };
};

export default usePositionFillRequestsService;
